import { OutputAndSource } from "../../ai/contexts/ai.rag.context";
import "./UnifiedSearch.scss";
import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import { FilterSetters } from "../elsearch.domain";
import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import { useTranslation } from "@me8eon/translation";
import { TSetShowAiAssist } from "../../ai-assist-refactored/AiAssist";
import LoaderComponent from "../../loader-component/LoaderComponent";
import { useTheme } from "@me8eon/themes";
import { useSearchTextFilter } from "@me8eon/all_filters";
import { ThemedIcon } from "@me8eon/icons";
import { lookupTableAboutChatbotActions } from "../../ai-assist-refactored/action-components/actions";
import { getFrom } from "../../../util/map";
import useAiData from "../../ai-assist-refactored/hooks/useAiData";
import { AiAssistContext } from "../../ai-assist-refactored/AiAssist.context";
import { MessageWithActionAndData } from "@me8eon/sessions_messages";
import { useFeatureFlag } from "@me8eon/featureflags";
import { useUserData } from "@me8eon/react_authentication";
import { extractQuestionSuggestions } from "@me8eon/messages";

const createMessagesForAiChatBot = (searchTerm: string, response: OutputAndSource): MessageWithActionAndData[] => {
    return [
        {
            role: "user",
            content: searchTerm,
            data: [],
            action: "user-query"
        },
        {
            role: "assistant",
            content: response.output,
            data: response.data || [],
            action: response.action || ""
        }
    ];
};

const AiChatBotResponse = ({ message, searchTerm, setShowAiAssist }: {
    message: OutputAndSource,
    searchTerm: string,
    setShowAiAssist: TSetShowAiAssist;
}) => {
    if (!message || !message?.action) return null;
    const AssistantComponent = getFrom(
        lookupTableAboutChatbotActions
    )(message?.action);

    if (!AssistantComponent) {
        console.error(`No component found for action: ${message?.action}`);
        return null;
    }
    const { setMessages, setMessagePassedFromWidget } = useContext(AiAssistContext);
    const { featureFlagEnabled } = useUserData();
    const t = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [showToggle, setShowToggle] = useState(false);
    const contentRef = React.useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        // The collapsed height from your CSS
        const MAX_COLLAPSED_HEIGHT = 80;
        if (contentRef.current) {
            const contentHeight = contentRef.current.scrollHeight;
            if (contentHeight > MAX_COLLAPSED_HEIGHT) {
                setShowToggle(true);
            } else {
                setShowToggle(false);
            }
        }
    }, [message.output]);

    return (
        <div className="chatbot-flex-wrapper" style={{ position: "relative" }}>
            {/* makes it invisible but still measurable */}
            <div className={`role-list`} ref={contentRef} style={{ position: 'absolute', visibility: "hidden", zIndex: -1 }}>
                <div className="unified-bot-text-format" dangerouslySetInnerHTML={{ __html: message.output }} />
            </div>
            <div className={`role-list ${expanded ? "expanded" : "collapsed"}`}>
                <div className="unified-bot-text-format">
                    <AssistantComponent
                        role="assistant"
                        content={message.output}
                        data={message?.data || []}
                        action={message?.action || ""}
                        enableNexthinkAction={false}
                    />
                </div>
            </div>
            <div className="show-more-button">
                <span style={{ background: "white", gap: "12px", display: "flex", alignItems: "center" }}>
                    {showToggle && (
                        <EonUiButton
                            title={expanded ? t("chatBot.showLessTooltip") : t("chatBot.showMoreTooltip")}
                            text={expanded ? t("chatBot.showLess") : t("chatBot.showMore")}
                            onClick={() => setExpanded(prev => !prev)}
                            size="small"
                            rank="functional"
                            scheme="darkgreyCarrier"
                            icon={expanded ? "chevron_small_up" : "chevron_small_down"}
                        />
                    )}
                    <EonUiButton
                        title={t("chatBot.continueAssistanceTooltip")}
                        text={t("chatBot.continueAssistance")}
                        onClick={() => {
                            const messageJson = JSON.stringify(
                                createMessagesForAiChatBot(searchTerm, message)
                            );
                            setMessagePassedFromWidget(messageJson);
                            setShowAiAssist(true);
                        }}
                        size="small"
                        rank="functional"
                        scheme="darkgreyCarrier"
                        icon="chevron_small_right"
                    />
                </span>
            </div>

        </div>
    );
};

const useSearchAiChatBot = (searchTerm: string): OutputAndSource | null => {
    const { selfService } = useAiData();
    const { setQuestionSuggestions } = useContext(AiAssistContext);
    const [response, setResponse] = useState<OutputAndSource | null>(null);

    useEffect(() => {
        const fetchResponse = async () => {
            const result: OutputAndSource = await selfService.handleAiQuery([], searchTerm, "user-query");
            const { content: cleanedContent, suggestions } = extractQuestionSuggestions(result.output);
            result.output = cleanedContent;
            setQuestionSuggestions(suggestions);
            setResponse(result);
        };
        if (searchTerm) {
            fetchResponse();
        }
    }, [searchTerm]);

    return response;
};

const UnifiedSearch = ({
    setters,
    setShowAiAssist,
}: {
    setters: FilterSetters;
    setShowAiAssist: TSetShowAiAssist;
}) => {
    const [searchTerm] = useSearchTextFilter();
    const aiResponse = useSearchAiChatBot(searchTerm);
    const { dataLayout } = useTheme();

    return (
        <div className="advance-unified-assist advance-unified-assist--flex">
            <>
                {aiResponse ? (
                    <div className="advance-unified-ai" style={dataLayout.AdvanceUnifiedBox}>
                        <div className="unified-box-heading">
                            <span className="ai-logo"><ThemedIcon name={"AiIcon"} /><span>Assistance</span></span>
                        </div>
                        <div className="unified-box-content" style={{ overflowY: "hidden" }}>
                            <AiChatBotResponse
                                searchTerm={searchTerm}
                                message={aiResponse}
                                setShowAiAssist={setShowAiAssist}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="no-response-loader">
                        <LoaderComponent isLoading={true} isLinear />
                    </div>
                )}
            </>
        </div>
    );
};
export default UnifiedSearch;
